import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

// ----------------------------------------------------------------------

const PwaContext = createContext(undefined);

const isStandaloneDisplay = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const waitForWaitingWorker = (registration) => {
  if (registration.waiting) return Promise.resolve(true);

  const worker = registration.installing;
  if (!worker) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      worker.removeEventListener('statechange', handleStateChange);
      window.clearTimeout(timeout);
      resolve(result);
    };

    const handleStateChange = () => {
      if (worker.state === 'installed') {
        window.setTimeout(() => finish(Boolean(registration.waiting)), 0);
      } else if (worker.state === 'activated' || worker.state === 'redundant') {
        finish(false);
      }
    };

    const timeout = window.setTimeout(() => finish(Boolean(registration.waiting)), 6000);
    worker.addEventListener('statechange', handleStateChange);
    handleStateChange();
  });
};

export function PwaProvider({ children }) {
  const registrationRef = useRef(null);
  const reloadingRef = useRef(false);
  const applyingUpdateRef = useRef(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);

  const isSupported = 'serviceWorker' in navigator;

  const inspectRegistration = useCallback((registration) => {
    registrationRef.current = registration;

    if (registration.waiting && navigator.serviceWorker.controller) {
      setUpdateAvailable(true);
    }

    const handleUpdateFound = () => {
      const worker = registration.installing;
      if (!worker) return;

      const handleStateChange = () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }
      };

      worker.addEventListener('statechange', handleStateChange);
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    return () => registration.removeEventListener('updatefound', handleUpdateFound);
  }, []);

  useEffect(() => {
    if (!isSupported || !import.meta.env.PROD) return undefined;

    let mounted = true;
    let removeRegistrationListener;

    const register = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/',
        });

        if (!mounted) return;
        removeRegistrationListener = inspectRegistration(registration);
      } catch (error) {
        console.error('ثبت سرویس‌ورکر رادو انجام نشد.', error);
      }
    };

    const handleControllerChange = () => {
      if (reloadingRef.current) return;
      if (!applyingUpdateRef.current) return;

      reloadingRef.current = true;
      window.location.reload();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        registrationRef.current?.update().catch(() => undefined);
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    register();

    const updateInterval = window.setInterval(
      () => registrationRef.current?.update().catch(() => undefined),
      30 * 60 * 1000
    );

    return () => {
      mounted = false;
      removeRegistrationListener?.();
      window.clearInterval(updateInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [inspectRegistration, isSupported]);

  useEffect(() => {
    const displayMode = window.matchMedia?.('(display-mode: standalone)');

    const handleInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
    };

    const handleDisplayMode = () => setIsStandalone(isStandaloneDisplay());

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    displayMode?.addEventListener?.('change', handleDisplayMode);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      displayMode?.removeEventListener?.('change', handleDisplayMode);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return { outcome: 'unavailable' };

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    return choice;
  }, [installPrompt]);

  const checkForUpdate = useCallback(async () => {
    const registration = registrationRef.current;
    if (!registration) return false;

    try {
      setIsCheckingUpdate(true);
      await registration.update();
      const hasUpdate = await waitForWaitingWorker(registration);
      if (hasUpdate) setUpdateAvailable(true);
      return hasUpdate;
    } finally {
      setIsCheckingUpdate(false);
    }
  }, []);

  const applyUpdate = useCallback(() => {
    const registration = registrationRef.current;
    if (!registration?.waiting) return false;

    applyingUpdateRef.current = true;
    setIsApplyingUpdate(true);
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  }, []);

  const value = useMemo(
    () => ({
      applyUpdate,
      checkForUpdate,
      installable: Boolean(installPrompt),
      isApplyingUpdate,
      isCheckingUpdate,
      isStandalone,
      isSupported,
      promptInstall,
      updateAvailable,
    }),
    [
      applyUpdate,
      checkForUpdate,
      installPrompt,
      isApplyingUpdate,
      isCheckingUpdate,
      isStandalone,
      isSupported,
      promptInstall,
      updateAvailable,
    ]
  );

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

export function usePwa() {
  const context = useContext(PwaContext);

  if (!context) {
    throw new Error('usePwa باید داخل PwaProvider استفاده شود.');
  }

  return context;
}
